import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { getMenuConfigs } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { useExternalAccessRights, useVenuePosIntegrationStatus } from 'common/hooks';
import useUser from 'common/hooks/useUser';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { languages } from 'config';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { VatCategoriesParamsContextProvider } from 'modules/menu/components/VatCategories';
import { useSharedMenuLink } from 'modules/menu/hooks/useSharedMenuLink';
import useRestockingRulesFeatureFlag from 'modules/menu/restocking-rules/useRestockingRulesFeatureFlag';
import { buildMenuFeatures, buildMenuPermissions } from 'modules/menu/utils';
import { BULK_IMAGE_UPLOAD_PAGE_PATH, GOOGLE_SHEETS_PAGE_PATH } from 'modules/shared-menu/constants';
import { OfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { OfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { GoogleSheetsImportPage } from 'modules/shared-menu/pages/GoogleSheetsImportPage';
import { VenueLevelMenuEditorPage } from 'modules/shared-menu/pages/VenueLevelMenuEditorPage/VenueLevelMenuEditorPage';
import { useVenueContext } from 'modules/venue/VenueContext';

import { SelectionInfoContextProps, SelectionInfoContextProvider } from '../contexts/SelectionInfoContext';
import { useLoadSelectionData } from '../hooks/useSelectionData';
import { SharedMenuBulkImageUploadPage } from '../pages/SharedMenuBulkImageUploadPage';
import { WoltSpecialsContextProvider } from '../pages/SharedMenuEditorPage/context/WoltSpecialsContext';
import { useLoadOverrideSettings } from '../pages/SharedMenuEditorPage/hooks/useSelectionOverrideSettings';
import { useLoadVenueSelectionOverrides } from '../pages/SharedMenuEditorPage/hooks/useSelectionOverrides';
import { useLoadVenueOfferCommissions } from '../pages/SharedMenuEditorPage/hooks/useVenueOfferCommissions';
import { CreateVenueSelection } from '../pages/VenueLevelMenuEditorPage/components/CreateVenueSelection';
import { LegacyCreateVenueSelection } from '../pages/VenueLevelMenuEditorPage/components/LegacyCreateVenueSelection';

type VenueListingManagerRootProps = {
  selectionId: string | undefined;
};

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const isLanguage = (language: any): language is Language => languages.includes(language);

/**
 * Detects if it's sub-routes like:
 * `{venueSlug}/menu-editor/google-sheets-import`
 * `{venueSlug}/menu-editor/bulk-image-upload`
 */
const isMenuEditorSubRoute = (pathname: string) => !pathname.endsWith('/menu-editor');

/**
 * This component is responsible for loading the initial selection data for the venue before rendering both the listing manager and its sub-routes that depend on the `Selection`
 * If no selection data is found, it will render the `CreateVenueSelection` component
 */
export const VenueListingManagerRoot = ({ selectionId }: VenueListingManagerRootProps) => {
  const [selectionIdToUse, setSelectionIdToUse] = useState(selectionId ?? '');
  const countryConfig = useCountryConfigContext();
  const scope = useScopeContext();
  const { sharedMenuLink } = useSharedMenuLink();
  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const venue = useVenueContext();
  const { country: venueCountry, city: venueCity, id: venueId } = venue;
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const { isEmployee } = useUser();

  const isSharedVenue = !!sharedMenuLink?.selection_is_shared;
  const isSingleVenue = !isSharedVenue;

  const { selectionData, loadingInitialSelectionData } = useLoadSelectionData({
    selectionId: selectionIdToUse,
    scope,
    enabled: !!selectionIdToUse,
  });

  const { loadingSelectionOverrides } = useLoadVenueSelectionOverrides({
    selectionId: selectionIdToUse,
    scope,
    enabled: isSharedVenue && !!selectionIdToUse,
  });

  const ownerScope = selectionData?.selection.scope;
  const { loadingOverrideSettings } = useLoadOverrideSettings({
    ownerScope,
    enabled: isSharedVenue && !!selectionData?.selection.scope,
  });

  const vatCategoriesParams = useMemo(() => ({ country: venueCountry, venueCity }), [venueCountry, venueCity]);
  const { enabled: restockingRulesEnabled } = useRestockingRulesFeatureFlag(venue.country);

  const { data: venuePosIntegrationStatus, isFetching: isFetchingPosIntegrationStatus } = useVenuePosIntegrationStatus(
    venue.id,
  );

  const vlmePermissions = useMemo(
    () => buildMenuPermissions(permissions, externalAccessRights, venue, venuePosIntegrationStatus),
    [permissions, externalAccessRights, venue, venuePosIntegrationStatus],
  );

  const { loadingCommissions } = useLoadVenueOfferCommissions({
    enabled: vlmePermissions.customCommissionRead,
  });

  const { data: menuConfigs } = useQuery(
    ['menu-configs', venue.menu],
    () => (venue.menu ? getMenuConfigs(venue.menu) : null),
    {
      enabled: !!venue.menu,
      staleTime: Infinity,
    },
  );
  const vlmeFeatures = useMemo(
    () =>
      buildMenuFeatures({
        venue,
        venueIsPosIntegrated: venuePosIntegrationStatus?.is_menu_integrated || false,
        hasCatalogSource: !!menuConfigs?.source_config,
        countryConfig,
        restockingRulesEnabled,
      }),
    [
      venue,
      venuePosIntegrationStatus?.is_menu_integrated,
      menuConfigs?.source_config,
      countryConfig,
      restockingRulesEnabled,
    ],
  );

  const vlmeConfig: SelectionInfoContextProps['vlmeConfig'] = useMemo(
    () => ({
      isSingleVenue,
      isSharedVenue,
      venueId,
    }),
    [isSharedVenue, isSingleVenue, venueId],
  );

  if (
    loadingInitialSelectionData ||
    loadingSelectionOverrides ||
    loadingOverrideSettings ||
    isFetchingPosIntegrationStatus ||
    loadingCommissions
  ) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  const isSubRouteAndMenuEmpty = isMenuEditorSubRoute(pathname) && !selectionData?.selection;

  if (isSubRouteAndMenuEmpty) {
    const [venueBasePath] = pathname.split('/menu-editor');
    return <Redirect to={`${venueBasePath}/menu-editor`} />;
  }

  if (!selectionData?.selection) {
    if (!isEmployee) {
      return (
        <LegacyCreateVenueSelection
          defaultLanguage={isLanguage(countryConfig.primary_language) ? countryConfig.primary_language : undefined}
          hasCreateMenuPermission={permissionUtils.hasCreateMenuPermission(permissions, externalAccessRights, venue)}
          onCreateSuccess={setSelectionIdToUse}
          venueId={venue.id}
          venueSlug={venue.slug}
        />
      );
    }

    return (
      <CreateVenueSelection
        defaultLanguage={isLanguage(countryConfig.primary_language) ? countryConfig.primary_language : undefined}
        hasCreateMenuPermission={permissionUtils.hasCreateMenuPermission(permissions, externalAccessRights, venue)}
        onCreateSuccess={setSelectionIdToUse}
        venueCurrency={venue.currency}
        venueId={venue.id}
        venueSlug={venue.slug}
      />
    );
  }

  return (
    <SelectionInfoContextProvider
      value={{
        country: venueCountry,
        selection: selectionData.selection,
        vlmeConfig,
      }}
    >
      <OfferingMenuPermissionsContext.Provider value={vlmePermissions}>
        <OfferingMenuFeaturesContext.Provider value={vlmeFeatures}>
          <VatCategoriesParamsContextProvider value={vatCategoriesParams}>
            <Switch>
              {isSingleVenue && (
                <Route path={`${path}/${BULK_IMAGE_UPLOAD_PAGE_PATH}`} exact>
                  <SharedMenuBulkImageUploadPage sharedMenu={selectionData.selection} />
                </Route>
              )}
              {vlmePermissions.contentEdit && isSingleVenue && (
                <Route path={`${path}/${GOOGLE_SHEETS_PAGE_PATH}`} exact>
                  <WoltSpecialsContextProvider>
                    <GoogleSheetsImportPage sharedMenu={selectionData.selection} />
                  </WoltSpecialsContextProvider>
                </Route>
              )}
              <Route path={path}>
                <VenueLevelMenuEditorPage selection={selectionData.selection} />
              </Route>
            </Switch>
          </VatCategoriesParamsContextProvider>
        </OfferingMenuFeaturesContext.Provider>
      </OfferingMenuPermissionsContext.Provider>
    </SelectionInfoContextProvider>
  );
};
